import React, {useMemo, RefObject, CSSProperties} from 'react';
import {gsap, throttle, useGrabbed} from 'helpers';

/**
 * Boundaries component for handling scroll interactions with a given target.
 *
 * @param {RefObject<HTMLElement | Window>} props.target - The target element or window for scroll interactions.
 */
export const Boundaries = ({target}: {target: RefObject<HTMLElement | Window>}) => {
  const notify = useMemo(() => throttle((direction: number) => {
    if (target.current === window) {
      const buffer = {y: window.pageYOffset};
      gsap.to(buffer, {
        ease: 'linear',
        duration: 0.1,
        y: `+=${direction * 50}`,
        onUpdate: () => window.scroll(0, buffer.y)
      });
    } else if (target.current) {
      gsap.to(target.current, {
        ease: 'linear',
        duration: 0.1,
        scrollTop: `+=${direction * 50}`
      });
    }
  }, 100), [target]);

  const grabbed = useGrabbed();
  const style = useMemo<CSSProperties | undefined>(() => grabbed && ({
    position: target.current === window ? 'fixed' : 'absolute',
    zIndex: 999,
    top: 0,
    left: `${(target.current === window && window.document.querySelector('aside')?.getBoundingClientRect().right) || 0}px`,
    right: 0,
    bottom: 0
  }), [grabbed, target]);

  return style ? (
    <>
      <div style={{...style, bottom: '90%'}} onDragOver={() => notify(-1)} />
      <div style={{...style, top: '90%'}} onDragOver={() => notify(+1)} />
    </>
  ) : null;
};
